import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginStart, loginSuccess, loginFailure, clearError } from '../authSlice';
import { authService } from '../authService';
import { getFirstPermissionRoute } from '../../../utils/permissionUtils';
import './LoginForm.css';
import Cookies from 'js-cookie';
import eyeClosed from '../../../assets/Icon/close-eyes.png';
import logoImage from '../../../assets/Icon/image.png';
import environment from '../../../config/environment';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      const user = JSON.parse(sessionStorage.getItem('user'));
      navigate(getFirstPermissionRoute(user));
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error && error !== 'Invalid or expired token') {
      setErrors({ general: error });
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      dispatch(loginStart()); 
      const response = await authService.login(formData.email, formData.password);
      console.log('[Login] API Response:', response);
      
      if (response && response.success) {
        // Store the complete response in session storage with name 'user'
        sessionStorage.setItem('user', JSON.stringify(response.data));
        
        if (response?.data?.password) {
          // Set cookie with more flexible settings for external access
          const isSecure = window.location.protocol === 'https:';
          const cookieOptions = { 
            secure: isSecure, 
            sameSite: 'lax' 
          };
          
          console.log('[Login] Setting cookie with options:', cookieOptions);
          Cookies.set('Token', response.data.password, cookieOptions);
          
          sessionStorage.setItem('id', response.data.id);
          if (response.data.first_name && response.data.last_name) {
            sessionStorage.setItem('first_name', response.data.first_name);
           // sessionStorage.setItem('user', JSON.stringify(response.data));
            sessionStorage.setItem('last_name', response.data.last_name);
            sessionStorage.setItem('username', response.data.first_name + ' ' + response.data.last_name);
          } else if (response.data.username) {
            sessionStorage.setItem('username', response.data.username);
          }
          if (response.data.email) {
            sessionStorage.setItem('email', response.data.email);
          }
        }
        dispatch(loginSuccess(response));
        const redirectPath = getFirstPermissionRoute(response.data);
        navigate(redirectPath);
      } else {
        setErrors({ general: 'Invalid username or password' });
        dispatch(loginFailure('Invalid username or password'));
      }
    } catch (error) {
      setErrors({ general: 'Invalid username or password' });
      dispatch(loginFailure('Invalid username or password'));
    }
  };

  const handleFocus = () => {
    dispatch(clearError());
    setErrors({});
  };

  return (
    <div className="login-root">
      <div className="login-left">
        <img 
          src={logoImage} 
          alt="Aesop Pricing Logo" 
          style={{ 
            width: '120px', 
            height: 'auto', 
            marginBottom: '20px',
            display: 'block'
          }} 
        />
        <h1 className="login-title">Hello,<br/>AESOP PRICING!</h1>
        <p className="login-desc">
          Lorem ipsum dolor sit amet consectetur. Aliquam tortor fermentum egestas id pellentesque mi nec nibh amet. Congue laoreet commodo dui tempor venenatis vitae.
        </p>
        <div className="login-footer-text">2025 AESOP. All rights reserved.</div>
      </div>
      <div className="login-right">
        <div className="login-card">
          <div className="login-header" style={{marginBottom: 24}}>
            <h2 className="login-header-title">Welcome Back!</h2>
            {/* <div style={{fontSize: 15, color: '#888', marginBottom: 8}}>
              Don't have an account? <span className="login-link" style={{fontWeight:600, cursor:'pointer'}} onClick={() => {}}>Create One</span>
            </div> */}
          </div>
          <form onSubmit={handleSubmit} className="login-form">
            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onFocus={handleFocus}
              className={errors.email ? 'login-input error' : 'login-input'}
              placeholder="Email"
              disabled={loading}
              style={{marginBottom: 16}}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
            <div style={{position:'relative'}}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={handleFocus}
                className={errors.password ? 'login-input error' : 'login-input'}
                placeholder="Password"
                disabled={loading}
                style={{marginBottom: 8}}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  fontSize: 18,
                  color: '#888'
                }}
                onMouseEnter={() => setShowPassword(true)}
                onMouseLeave={() => setShowPassword(false)}
                tabIndex={0}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : <img src={eyeClosed} alt="Hide password" style={{ width: 22, height: 22 }} />}
              </span>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
              style={{width:'100%', marginTop: 16}}
            >
              {loading ? 'Signing in...' : 'Login'}
            </button>
          </form>
          <div className="login-footer-link" style={{marginTop: 24, textAlign:'center'}}>
            <span style={{color:'#888'}}>Forgot Password? </span>
            <span className="login-link" onClick={() => navigate('/forgot-password')} style={{cursor:'pointer', color:'#222', fontWeight:600}}>Click Here</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm; 